import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { logger } from '../../../utils/logger';
import { getAssessmentById } from '../api/getAssessmentById';
import { getAssessmentReport } from '../api/getAssessmentReport';
import { AssessmentReportViewModel } from '../models/assessmentReportView';
import { transformAssessmentReport } from '../utils/transformAssessmentReport';

export type AssessmentResultsState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'threshold_not_met';
      title: string;
      respondentCount: number;
      minimumResponsesForAggregate: number;
      message: string;
    }
  | { status: 'success'; report: AssessmentReportViewModel }
  | { status: 'not_found' };

export const useAssessmentResults = (assessmentId?: string, orgId?: string): AssessmentResultsState => {
  const [reportState, setReportState] = useState<AssessmentResultsState>({ status: 'loading' });

  useEffect(() => {
    if (!assessmentId || !orgId) {
      setReportState({ status: 'error' });
      return;
    }

    setReportState({ status: 'loading' });

    getAssessmentReport({
      orgId,
      assessmentId,
      since: undefined,
      until: undefined,
    })
      .then(reportResponse => {
        if ('minimumResponsesForAggregate' in reportResponse) {
          setReportState({
            status: 'threshold_not_met',
            title: reportResponse.assessmentName,
            respondentCount: reportResponse.respondentCount,
            minimumResponsesForAggregate: reportResponse.minimumResponsesForAggregate,
            message: reportResponse.message,
          });
          return;
        }

        return getAssessmentById(assessmentId).then(assessment =>
          setReportState({
            status: 'success',
            report: transformAssessmentReport(reportResponse, assessment),
          })
        );
      })
      .catch((error: AxiosError) => {
        logger.error(error);

        if (error.response?.status === 404) {
          setReportState({ status: 'not_found' });
          return;
        }

        setReportState({ status: 'error' });
      });
  }, [assessmentId, orgId]);

  return reportState;
};
